import {Box, Divider, Stack, Typography} from "@mui/material";
import React from "react";
import {DisplayUnitsCount} from "../../booking-utils";
import {B2BoxType, B2Site} from "../../data/client.gen";
import {useLocalization} from "../../i18n";
import {DisplayCurrency} from "../../utils";

// Box-type ordering by physical size, mirroring the order flow (order-boxes.tsx).
const SIZE_ORDER: Record<string, number> = {XS: 100, S: 200, M: 300, L: 400, XL: 500, XXL: 600, SKI: 600};

function typeToSize(type: string) {
	return SIZE_ORDER[type] ?? 0;
}

function sortedEntries(map: unknown) {
	return Object.entries((map ?? {}) as Record<string, number>)
		.map(([key, price]) => ({key: parseInt(key, 10), price}))
		.sort((a, b) => a.key - b.key);
}

export function PriceList({site}: { site: B2Site }) {
	const {t} = useLocalization();
	const {boxes = [], currency, bookingUnit = "Days", pricingMode = "Unit"} = site;

	const sortedBoxes = [...boxes].sort((a, b) => typeToSize(a.type) - typeToSize(b.type));

	if (sortedBoxes.length === 0) {
		return null;
	}

	return (
		<Stack direction="column" gap={1}>
			<Typography variant="subtitle1">{t("storefront.prices.title")}</Typography>
			<Stack direction="column" divider={<Divider flexItem />} gap={1.5}>
				{sortedBoxes.map(box => (
					<BoxPrices
						key={box.type}
						box={box}
						currency={currency}
						bookingUnit={bookingUnit}
						pricingMode={pricingMode}
					/>
				))}
			</Stack>
		</Stack>
	);
}

function BoxPrices({box, currency, bookingUnit, pricingMode}: {
	box: B2BoxType;
	currency: B2Site["currency"];
	bookingUnit: NonNullable<B2Site["bookingUnit"]>;
	pricingMode: NonNullable<B2Site["pricingMode"]>;
}) {
	const {t} = useLocalization();

	const rows = pricingMode === "Package"
		? sortedEntries(box.packagePrice).map(({key, price}) => ({
			label: <DisplayUnitsCount unit={bookingUnit} count={key} />,
			price,
		}))
		: sortedEntries(box.unitPrices).map(({key, price}) => ({
			label: (
				<span>
					{t("storefront.prices.from")} <DisplayUnitsCount unit={bookingUnit} count={key} />
				</span>
			),
			price,
		}));

	const perUnitSuffix = pricingMode === "Unit"
		? `/ ${bookingUnit === "Hours" ? t("dates.hour") : t("dates.day")}`
		: undefined;

	return (
		<Stack direction="column" gap={0.5}>
			<Typography variant="subtitle2">
				<small>{t("boxes.type")}</small> <strong>{box.type}</strong>
			</Typography>
			{rows.length === 0
				? <Typography variant="body2" color="text.secondary">{t("storefront.prices.unavailable")}</Typography>
				: rows.map((row, i) => (
					<Stack key={i} direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
						<Typography variant="body2" color="text.secondary">{row.label}</Typography>
						<Typography variant="body2">
							<small>{currency}</small>&nbsp;<DisplayCurrency amount={row.price} />
							{perUnitSuffix && <small>&nbsp;{perUnitSuffix}</small>}
						</Typography>
					</Stack>
				))}
		</Stack>
	);
}
